import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import useSessionContext from '../../data/queryHooks/useSessionContext';
import {
	GetServiceLocations,
	SendPendingAshlinkUserInvitationFn,
} from '../../data/apiCalls';
import CreateSubAccountTablePage from './shared/SubAccountLocationsTable';
import LoadingButton from './shared/LoadingButton';

const SlideLeft = props => <Slide {...props} direction='left' />;

const CreateSubAccount = () => {
	const [alertText, setAlertText] = useState({ title: '', description: '' });
	const [openSnackbar, setOpenSnackbar] = useState('');
	const [currentPage, setCurrentPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [searchStr, setSearchStr] = useState('');
	const {
		userInfo,
		ashlinkUserEntityType,
		ashlinkUserId,
		isTest,
		providerId,
		clinicId,
		specialtyCode,
	} = useSessionContext();

	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { mutate: sendPendingAshlinkUserInvitation, isLoading: isFetching } =
		useMutation(values => SendPendingAshlinkUserInvitationFn(...values), {
			onSuccess: email => {
				queryClient.invalidateQueries('GetPendingSubAccounts');
				sessionStorage.setItem('isCreate', true);
				navigate('/account/manageSubAccounts', {
					state: { email },
				});
			},
			onError: () => {
				const text = {
					title: 'Sorry, something went wrong.',
					description: `We were unable to process your request.`,
				};
				setAlertText(text);
				setOpenSnackbar('error');
			},
		});

	const { isLoading, data } = useQuery(
		['allClinics', currentPage, pageSize, searchStr],
		() =>
			GetServiceLocations(
				ashlinkUserEntityType,
				ashlinkUserId,
				providerId,
				clinicId,
				searchStr,
				currentPage + 1, // endpoint is 1 indexed, but data grid is 0 indexed
				pageSize ?? 10 // default to 10 if pageSize is not set (this is the data grid default)
			)
	);

	const locations = data?.data?.data?.ashlinkUserProfiles ?? [];
	const totalRows = data?.data?.data?.totalRows ?? 0;

	const handleClose = reason => {
		if (reason === 'clickaway') {
			return;
		}
		setOpenSnackbar('');
	};
	const validationSchema = Yup.object().shape({
		emailAddress: Yup.string()
			.email('Please enter a valid email address')
			.required('Please enter a valid email address')
			.max(100, 'Email address cannot exceed 100 characters'),
		confirmEmailAddress: Yup.string()
			.oneOf(
				[Yup.ref('emailAddress'), null],
				'Email address does not match'
			)
			.required('Email address does not match'),
		locations: Yup.array().when('accessLevel', {
			is: 1,
			then: () => Yup.array(),
			otherwise: () =>
				Yup.array().min(1, 'Please select at least one location'),
		}),
		accessLevel: Yup.string(),
	});

	const handleSubmit = async values => {
		await sendPendingAshlinkUserInvitation([
			values.emailAddress,
			values.accessLevel,
			isTest,
			providerId,
			specialtyCode,
			clinicId,
			values.locations,
		]);
	};

	return (
		<>
			<Paper elevation={0}>
				<Formik
					initialValues={{
						emailAddress: '',
						confirmEmailAddress: '',
						locations: [],
						accessLevel: 3,
					}}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
				>
					{({
						setFieldValue,
						values,
						touched,
						errors,
						handleChange,
						handleBlur,
					}) => (
						<Form>
							<Stack sx={{ py: 3.5, px: 4 }}>
								<Typography variant='h5' component='h1'>
									Create New Sub Account
								</Typography>
								<Divider sx={{ my: 2 }} />
								<Typography mb={4.5}>
									Fill out the information below to send an
									invite for your staff to create a new sub
									account.
								</Typography>
								<TextField
									label='Sub Account Email Address'
									data-testid='email-address'
									value={values.emailAddress}
									name='emailAddress'
									onChange={handleChange}
									onBlur={handleBlur}
									error={
										touched.emailAddress &&
										Boolean(errors.emailAddress)
									}
									helperText={
										touched.emailAddress &&
										errors.emailAddress
									}
									sx={{
										mb: 4.25,
										width: { xs: '100%', md: 440 },
									}}
								/>

								<TextField
									label='Confirm Sub Account Email Address'
									data-testid='confirmEmail-address'
									value={values.confirmEmailAddress}
									name='confirmEmailAddress'
									onChange={handleChange}
									onBlur={handleBlur}
									error={
										touched.confirmEmailAddress &&
										Boolean(errors.confirmEmailAddress)
									}
									helperText={
										touched.confirmEmailAddress &&
										errors.confirmEmailAddress
									}
									sx={{
										mb: 1,
										width: { xs: '100%', md: 440 },
									}}
								/>

								<CreateSubAccountTablePage
									pageTitle='Select Locations:'
									locations={locations}
									isLoading={isLoading}
									selectionModelUpdate={selections =>
										setFieldValue('locations', selections)
									}
									error={
										touched.locations && errors.locations
									}
									page={currentPage}
									pageSize={pageSize}
									onPageChange={newPage =>
										setCurrentPage(newPage)
									}
									totalRows={totalRows}
									onPageSizeChange={newPageSize => {
										setPageSize(newPageSize);
									}}
									onSearchStrChange={newSearchStr =>
										setSearchStr(newSearchStr)
									}
								/>

								<Box
									sx={{
										mt: 6,
									}}
								>
									<FormControl
										component='fieldset'
										id='accessLevel-group'
										error={
											touched.accessLevel &&
											Boolean(errors.accessLevel)
										}
									>
										<Typography
											variant='h6'
											component='label'
											id='accessLevel-label'
											sx={{ mb: 2.5 }}
										>
											Select the ASHLink Access Level:
										</Typography>
										<RadioGroup
											defaultValue={3}
											aria-labelledby='accessLevel-label'
											name='accessLevel'
											onChange={handleChange}
										>
											<FormControlLabel
												value={1}
												control={<Radio />}
												label={
													<Typography>
														<strong>
															Level 1:{' '}
														</strong>
														Users are granted full
														access to all features
														of ASHLink with the
														exception of{' '}
														{[
															400, 401, 402, 500,
														].includes(
															userInfo.clubTypeID
														)
															? 'quarterly validations and see '
															: 'seeing '}
														alerts and warnings.
													</Typography>
												}
											/>
											<FormControlLabel
												value={2}
												control={<Radio />}
												label={
													<Typography>
														<strong>
															Level 2:{' '}
														</strong>
														Same permissions as
														Level 1 - With the
														exception of inviting
														and creating sub
														accounts.
													</Typography>
												}
											/>
											<FormControlLabel
												value={3}
												control={<Radio />}
												label={
													<Typography>
														<strong>
															Level 3:{' '}
														</strong>
														User access is limited
														to checking eligibility
													</Typography>
												}
											/>
										</RadioGroup>
									</FormControl>
								</Box>

								<Box>
									<Typography
										sx={{
											fontStyle: 'italic',
											mt: 6,
											mb: 3.5,
										}}
									>
										If you have any questions, please select
										&lsquo;Help&lsquo; located on the blue
										task bar above and &lsquo;Submit an
										online Question or Comment&lsquo;.
										Responses can be viewed in your
										&lsquo;Inbox&lsquo;.
									</Typography>
								</Box>

								<Stack direction='row'>
									<LoadingButton
										type='submit'
										variant='contained'
										isLoading={isFetching}
										sx={{
											mr: 2,
										}}
									>
										Create new sub account
									</LoadingButton>
									<Button
										type='button'
										variant='outlined'
										onClick={() =>
											navigate(
												'/account/manageSubAccounts'
											)
										}
									>
										Cancel
									</Button>
								</Stack>
							</Stack>
						</Form>
					)}
				</Formik>
			</Paper>
			<Snackbar
				open={!!openSnackbar}
				autoHideDuration={10000}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				TransitionComponent={SlideLeft}
				sx={{
					boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
				}}
			>
				<Alert severity='error'>
					<Typography fontWeight={700}>{alertText.title}</Typography>
					<Typography>{alertText.description}</Typography>
				</Alert>
			</Snackbar>
		</>
	);
};
export default CreateSubAccount;
